import { Immutable } from 'immer';

import { createCommands } from '@tdp/common/commandHandler';
import { notificationSlice, NotificationType } from '@tdp/common/notification';
import { ILogger } from '@tdp/common/telemetry';

import { BotEventSubscription } from './models.eventSubscription';
import { IBotEventSubscriptionProvider, IBotEventSubscriptionWriter } from './services.eventSubscription';
import { botEventSubscriptionSlice } from './slice.eventSubscription';

export interface CreateBotEventSubscriptionCommandHandlerDependencies {
  logger: ILogger;
  botEventSubscriptionProvider: IBotEventSubscriptionProvider;
  botEventSubscriptionWriter: IBotEventSubscriptionWriter;
}

export const createBotEventSubscriptionCommandHandler = ({
  logger,
  botEventSubscriptionProvider,
  botEventSubscriptionWriter,
}: CreateBotEventSubscriptionCommandHandlerDependencies) =>
  createCommands({
    name: 'botEventSubscriptionCommands',
    handlers: {
      async *loadBotEventSubscription(botId: string) {
        const result = await botEventSubscriptionProvider.getBotEventSubscription(botId);

        if (result.type === 'networkError') {
          logger.error(
            'Error loading bot event subscription for bot id: %s | status: %d',
            botId,
            result.errorCode
          );
          yield notificationSlice.actions.showNotification({
            message: `Failed to load bot event subscription: ${botId} | Network error (status: ${result.errorCode.toString()})`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }

        if (result.type === 'parseError') {
          logger.error(
            'Error parsing bot event subscription for bot id: %s | error: %s',
            botId,
            result.errors.message
          );
          yield notificationSlice.actions.showNotification({
            message: `Failed to load bot event subscription: ${botId} | Parse error`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }

        // Happy path - successful result
        yield botEventSubscriptionSlice.actions.subscriptionLoaded(result.subscription);
      },
      async *updateBotEventSubscription({ botId, subscription }: { botId: string; subscription: Immutable<BotEventSubscription> }) {
        const result = await botEventSubscriptionWriter.putBotEventSubscription(botId, subscription);

        if (result.type === 'networkError') {
          logger.logUserBi({ telemetryMarker: 'api-action-bot-event-subscription-update-failure', message: `Failed update event subscription for botId ${botId}` });
          logger.error(
            'Error updating bot event subscription for bot id: %s | status: %d',
            botId,
            result.errorCode
          );
          yield notificationSlice.actions.showNotification({
            message: `Failed to update bot event subscription: ${botId} | Network error (status: ${result.errorCode.toString()})`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }

        if (result.type === 'parseError') {
          logger.logUserBi({ telemetryMarker: 'api-action-bot-event-subscription-update-failure', message: `Failed parsing update event subscription response for botId ${botId}` });

          logger.error(
            'Error parsing bot event subscription response for bot id: %s | error: %s',
            botId,
            result.errors.message
          );
          yield notificationSlice.actions.showNotification({
            message: `Failed to update bot event subscription: ${botId} | Parse error`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }

        // Happy path - successful result
        yield botEventSubscriptionSlice.actions.subscriptionLoaded(result.subscription);
        logger.logUserBi({ telemetryMarker: 'api-action-bot-event-subscription-update-success', message: `Successfully updated event subscription for botId ${botId}` });
      },
    },
  });
